import React, { useState, useMemo, useEffect, useContext } from "react";
import { useTranslation } from "react-i18next";
import AdvTaskListingUserDetailDto from "../../../../types/dtos/dashboards/manager/listings/adv-tasks/AdvTaskListingUserDetailDto";
import AdvTaskListingDto from "../../../../types/dtos/dashboards/manager/listings/adv-tasks/AdvTaskListingDto";
import { AccordionItem } from "../../../common/Accordion";
import Avatar from "../../../common/Avatar";
import AdvTaskUserTaskItem from "./AdvTaskUserTaskItem";
import PaginationControls from "../../../common/PaginationControls";
import { TaskResetPaginationContext } from "./AdvTaskUserView";
import { ClientTaskType } from "../../../../types/dtos/tasks/advanced-tasks/ClientTaskType";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus } from "@fortawesome/pro-regular-svg-icons";
import TaskManagementRestrictionBanner from "../../../task-management/TaskManagementRestrictionBanner";
import taskRestrictionHelper from "../../../../helpers/taskRestrictionHelper";

interface AdvTaskUserListItemProps {
  user: AdvTaskListingUserDetailDto;
  tasks: AdvTaskListingDto[];
  originalTasks?: AdvTaskListingDto[] | null;
  taskType?: ClientTaskType | null;
  onTaskClick: (taskId: string) => void;
  onClearNotifications?: () => void;
  hasNotifications?: boolean;
  onAddTask?: (user: AdvTaskListingUserDetailDto) => void;
}

const AdvTaskUserListItem: React.FC<AdvTaskUserListItemProps> = ({
  user,
  tasks,
  originalTasks,
  taskType,
  onTaskClick,
  onClearNotifications,
  hasNotifications = false,
  onAddTask,
}) => {
  const defaultPageSize = 5; // Default page size for pagination
  const { t } = useTranslation();
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(defaultPageSize);

  // Hide the add new button if the task type is set to suppress the dashboard create and validate button
  const hideAddNewButton =
    taskType != null && taskType.suppressDashboardCreateAndValidate;

  // Check if the user has a maximum task limit
  // and if the number of active tasks exceeds that limit
  const activeTasks = tasks.filter(
    (task) =>
      task.taskStatus === "ACTIVE" && task.ownerEmployeeId === user.employeeId
  );
  const reachedMaximumTaskLimit =
    user.maxTasks != null && activeTasks.length >= user.maxTasks;

  // Get the reset counter from context
  const resetPaginationCounter = useContext(TaskResetPaginationContext);

  // Filter tasks that belong to this user (for display purposes)
  const userTasks = useMemo(
    () => tasks.filter((task) => task.ownerEmployeeId === user.employeeId),
    [tasks, user.employeeId]
  );

  // Filter ALL tasks that belong to this user (for static counts - not affected by filtering)
  const allUserTasks = useMemo(
    () =>
      (originalTasks || tasks).filter(
        (task) => task.ownerEmployeeId === user.employeeId
      ),
    [originalTasks, tasks, user.employeeId]
  );

  // Get paginated tasks
  const paginatedTasks = useMemo(() => {
    const startIndex = (pageNumber - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return userTasks.slice(startIndex, endIndex);
  }, [userTasks, pageNumber, pageSize]);

  // Reset page number when:
  // 1. Individual user's tasks change
  // 2. Global reset is triggered (via context)
  useEffect(() => {
    setPageNumber(1);
  }, [tasks, user.employeeId, resetPaginationCounter]);

  // Handler for adding a new task
  const handleAddTaskClick = () => {
    if (onAddTask) {
      onAddTask(user);
    }
  };

  // Handler for page size changes - also resets current page to 1
  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);
    setPageNumber(1);
  };

  // Calculate status counts for display badges (filtered)
  const statusCounts = {
    active: userTasks.filter((t) => t.overallStatus === "ACTIVE").length,
    completed: userTasks.filter((t) => t.overallStatus === "COMPLETED").length,
    overdue: userTasks.filter((t) => t.overallStatus === "OVERDUE").length,
    historic: userTasks.filter((t) => t.overallStatus === "HISTORIC").length,
    cancelled: userTasks.filter((t) => t.overallStatus === "CANCELLED").length,
  };

  // Calculate STATIC status counts for summary row (unfiltered)
  const staticStatusCounts = {
    active: allUserTasks.filter((t) => t.overallStatus === "ACTIVE").length,
    completed: allUserTasks.filter((t) => t.overallStatus === "COMPLETED")
      .length,
    overdue: allUserTasks.filter((t) => t.overallStatus === "OVERDUE").length,
    historic: allUserTasks.filter((t) => t.overallStatus === "HISTORIC").length,
    cancelled: allUserTasks.filter((t) => t.overallStatus === "CANCELLED")
      .length,
  };

  // Calculate next due date for active tasks (using static counts)
  const getNextDueDate = () => {
    const activeTasks = allUserTasks.filter(
      (t) => t.overallStatus === "ACTIVE" && t.targetDate
    );

    if (activeTasks.length === 0) return null;

    // Find the earliest target date
    const sortedByDate = activeTasks.sort(
      (a, b) =>
        new Date(a.targetDate!).getTime() - new Date(b.targetDate!).getTime()
    );

    return sortedByDate[0].targetDate;
  };

  const nextDueDate = getNextDueDate();
  const formattedNextDueDate = nextDueDate
    ? new Date(nextDueDate).toLocaleDateString("en-GB")
    : null;

  // Check for task restrictions and return a banner if needed
  const renderTaskRestrictionBanner = () => {
    if (!taskType) return null;

    let taskRestrictionText: string | null = null;
    let showRestrictionBanner: boolean = false;

    // Get relevant properties from the user DTO
    const minTasks = user.minTasks || null;
    const maxTasks = user.maxTasks || null;
    const countRestriction = user.countRestriction;
    const activeCategories = user.activeCategories || null;

    const doesUserHaveTaskRestrictions =
      minTasks != null ||
      maxTasks != null ||
      activeCategories?.find((x) => x.minTaskCount || x.maxTaskCount) != null;

    if (doesUserHaveTaskRestrictions) {
      taskRestrictionText =
        taskRestrictionHelper.getRestrictionBannerTextHtmlForManagementPages(
          taskType,
          activeCategories,
          countRestriction,
          minTasks,
          maxTasks,
          user.employeeFirstName,
          t
        );

      // If the task type is set to suppress validation, then we don't show it
      showRestrictionBanner = taskType.suppressDashboardCreateAndValidate
        ? false
        : !taskRestrictionHelper.doesUserHaveTasksThatSatisfyRestrictions(
            userTasks,
            minTasks,
            maxTasks,
            countRestriction,
            activeCategories
          ).satisfied;
    }

    return showRestrictionBanner && taskRestrictionText ? (
      <TaskManagementRestrictionBanner bannerTextHtml={taskRestrictionText} />
    ) : null;
  };

  const renderUserHeader = () => {
    return (
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between w-full p-4">
        <div className="flex items-center mb-2 sm:mb-0">
          <div className="mr-3">
            <Avatar
              imageUrl={user.profileImageUrl}
              userFullName={user.employeeFullName}
              userInitials={user.initials}
              size={10}
              activeStatus="DO-NOT-SHOW"
            />
          </div>
          <div>
            <p className="font-medium text-lg text-gray-800">
              {user.employeeFullName}
            </p>
            <p className="text-sm text-gray-600">
              {user.jobTitleTranslationKeyIdentifier
                ? t(user.jobTitleTranslationKeyIdentifier)
                : ""}
            </p>
            {/* Task Summary Row */}
            <div className="text-xs text-gray-500 mt-1 flex items-center flex-wrap gap-2">
              <span className="flex items-center">
                Active{" "}
                {taskType?.pluralNameTranslationKeyIdentifier
                  ? t(taskType.pluralNameTranslationKeyIdentifier).toLowerCase()
                  : "goals"}
                : {staticStatusCounts.active}
              </span>
              {staticStatusCounts.overdue > 0 && (
                <span className="text-red-600">
                  | Overdue{" "}
                  {taskType?.pluralNameTranslationKeyIdentifier
                    ? t(
                        taskType.pluralNameTranslationKeyIdentifier
                      ).toLowerCase()
                    : "goals"}
                  : {staticStatusCounts.overdue}
                </span>
              )}
              {formattedNextDueDate && (
                <span>
                  | Next{" "}
                  {taskType?.singularNameTranslationKeyIdentifier
                    ? t(
                        taskType.singularNameTranslationKeyIdentifier
                      ).toLowerCase()
                    : "goal"}{" "}
                  due: {formattedNextDueDate}
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="flex flex-wrap gap-2 mt-2 sm:mt-0">
          {statusCounts.completed > 0 && (
            <span className="px-2 py-1 bg-green-100 text-green-700 rounded-md text-xs">
              Completed: {statusCounts.completed}
            </span>
          )}
          {statusCounts.cancelled > 0 && (
            <span className="px-2 py-1 bg-red-100 text-red-700 rounded-md text-xs">
              Cancelled: {statusCounts.cancelled}
            </span>
          )}
        </div>
      </div>
    );
  };

  // Expanded content - Task list with pagination
  const renderTaskList = () => (
    <div className="border-t border-gray-200 p-4">
      {userTasks.length > 0 ? (
        <>
          {/* Action Buttons */}
          <div className="flex justify-between items-center mb-3">
            <div>
              {/* Add Task button */}
              {onAddTask && taskType != null && !hideAddNewButton && (
                <button
                  className="btn-primary text-sm px-3 py-1 flex items-center disabled:cursor-not-allowed"
                  onClick={handleAddTaskClick}
                  disabled={reachedMaximumTaskLimit}
                >
                  <FontAwesomeIcon icon={faPlus} className="mr-1" />
                  New {t(taskType.singularNameTranslationKeyIdentifier)}
                </button>
              )}
            </div>
            <div className="flex items-center space-x-2">
              {hasNotifications && onClearNotifications && (
                <button
                  className="btn-secondary border-gray-300 text-[#5d5d5d] text-xs"
                  onClick={onClearNotifications}
                >
                  Clear notifications
                </button>
              )}
            </div>
          </div>

          {/* Restriction Banner */}
          {renderTaskRestrictionBanner()}

          {/* Render task list */}
          {paginatedTasks.map((task) => (
            <AdvTaskUserTaskItem
              key={`task-${task.userTaskId}`}
              task={task}
              taskType={taskType}
              onTaskClick={(taskId) => onTaskClick(taskId)}
            />
          ))}

          {/* Always show pagination controls when there are tasks and the number of tasks are 
              bigger than the default page size */}
          {userTasks.length > defaultPageSize && (
            <div className="mt-4">
              <PaginationControls
                activePage={pageNumber}
                itemsPerPage={pageSize}
                totalItemCount={userTasks.length}
                isLoading={false}
                loadingText={t("Common.Loading") + "..."}
                onPageChange={setPageNumber}
                onPageSizeChange={handlePageSizeChange}
              />
            </div>
          )}
        </>
      ) : (
        <div className="text-center text-gray-500 py-2">
          {/* Empty state with Add Task button */}
          <p className="mb-4">
            No{" "}
            {taskType
              ? t(taskType.pluralNameTranslationKeyIdentifier).toLowerCase()
              : "tasks"}{" "}
            available
          </p>
          {onAddTask && taskType != null && !hideAddNewButton && (
            <button
              className="btn-primary text-sm"
              onClick={handleAddTaskClick}
              disabled={reachedMaximumTaskLimit}
            >
              <FontAwesomeIcon icon={faPlus} className="mr-2" />
              New {t(taskType.singularNameTranslationKeyIdentifier)}
            </button>
          )}
        </div>
      )}
    </div>
  );

  return (
    <AccordionItem
      itemValue={`user-${user.employeeId}`}
      trigger={renderUserHeader()}
      content={renderTaskList()}
      classNames="bg-white rounded-lg shadow-sm border border-gray-200 mb-4 overflow-hidden"
      triggerClassNames="w-full text-left"
      triggerBackgroundColourClassNames="bg-gray-50"
      showChevronIcons={true}
      iconClassNames="mr-4 text-gray-500"
    />
  );
};

export default AdvTaskUserListItem;
